import React, { useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { instructionBodyTextSx, variableTokenChipInteractiveSx } from '../markdownBodySx';
import { mergeVariablesFromTemplate, buildVariablePlaceholderRegex } from '../../../utils/promptVariables';

/**
 * Escapes HTML special characters in a string.
 */
function escapeHtml(str = '') {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build HTML with variable placeholders highlighted and clickable.
 * - `text` is the raw prompt text (with {{var}} placeholders).
 * - `variables` is an array of variable definitions ({ name, defaultValue, ... }).
 * - `variableValues` is a map of variable name -> current value.
 */
function buildHtmlWithVariables(text, variables = [], variableValues = {}) {
  if (!text) return '';

  const source = String(text);
  const matches = [];

  (variables || []).forEach((variable) => {
    if (!variable?.name) return;
    const pattern = buildVariablePlaceholderRegex(variable.name, 'gi');
    let match;
    while ((match = pattern.exec(source)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        variable,
      });
    }
  });

  if (!matches.length) return escapeHtml(source).replace(/\n/g, '<br>');

  matches.sort((a, b) => a.start - b.start || a.end - b.end);
  let cursor = 0;
  let html = '';

  matches.forEach(({ start, end, variable }) => {
    if (start < cursor) return;
    const name = variable.name;
    const raw = variableValues[name];
    const hasValue = raw !== undefined && raw !== null && String(raw).trim() !== '';
    const displayValue = hasValue ? String(raw) : (variable.defaultValue || `{{${name}}}`);

    html += escapeHtml(source.slice(cursor, start)).replace(/\n/g, '<br>');
    html += `<span class="var-token" data-var-name="${escapeHtml(name)}" contenteditable="false">${escapeHtml(displayValue)}</span>`;
    cursor = end;
  });

  return html + escapeHtml(source.slice(cursor)).replace(/\n/g, '<br>');
}

const EDITOR_BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE']);

/** Empty block Chrome uses for a blank line, e.g. `<div><br></div>`. */
function isBlankLineBlock(node) {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
  if (!EDITOR_BLOCK_TAGS.has(node.tagName)) return false;
  const children = Array.from(node.childNodes);
  if (children.length === 0) return true;

  const elementChildren = children.filter((child) => child.nodeType === Node.ELEMENT_NODE);
  const text = children
    .filter((child) => child.nodeType === Node.TEXT_NODE)
    .map((child) => child.textContent || '')
    .join('');

  if (!text.trim() && elementChildren.length === 0) return true;
  if (!text.trim() && elementChildren.every((child) => child.tagName === 'BR')) return true;
  return false;
}

/**
 * Read contentEditable root back to canonical prompt text, preserving line breaks.
 */
function serializeEditableToPrompt(root) {
  if (!root) return '';
  let result = '';

  const walk = (node, isRoot = false) => {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent || '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    if (node.classList?.contains('var-token')) {
      const name = node.getAttribute('data-var-name');
      result += name ? `{{${name}}}` : (node.textContent || '');
      return;
    }
    if (node.tagName === 'BR') {
      result += '\n';
      return;
    }

    const isBlock = EDITOR_BLOCK_TAGS.has(node.tagName);

    // Blank-line blocks already include a line break; avoid counting <br> + trailing block newline too.
    if (isBlock && !isRoot && isBlankLineBlock(node)) {
      if (result.length > 0 && !result.endsWith('\n')) {
        result += '\n';
      }
      result += '\n';
      return;
    }

    if (isBlock && !isRoot && result.length > 0 && !result.endsWith('\n')) {
      result += '\n';
    }

    Array.from(node.childNodes).forEach((child) => walk(child, false));

    if (isBlock && !isRoot && node.nextSibling) {
      result += '\n';
    }
  };

  walk(root, true);
  return result;
}

/** True if prompt text contains a full {{name}} for some defined variable (case-insensitive). */
function needsPlaceholderTokens(text, variables) {
  if (!text || !variables?.length) return false;
  return variables.some((variable) => (
    variable?.name?.trim()
      && buildVariablePlaceholderRegex(variable.name, 'i').test(text)
  ));
}

/** How many complete {{var}} occurrences in text match a defined variable name. */
function countDefinedPlaceholdersInText(text, variables) {
  if (!text || !variables?.length) return 0;
  let count = 0;
  variables.forEach((variable) => {
    if (!variable?.name?.trim()) return;
    const pattern = buildVariablePlaceholderRegex(variable.name, 'gi');
    const matches = String(text).match(pattern);
    if (matches) count += matches.length;
  });
  return count;
}

function isEditorFocused(root) {
  if (!root) return false;
  const active = document.activeElement;
  return active === root || root.contains(active);
}

/** Caret offset in canonical prompt text (matches serializeEditableToPrompt). */
function getCaretTextOffset(root) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!root?.contains(range.startContainer)) return null;

  const preCaretRange = range.cloneRange();
  preCaretRange.selectNodeContents(root);
  preCaretRange.setEnd(range.startContainer, range.startOffset);

  const container = document.createElement('div');
  container.appendChild(preCaretRange.cloneContents());
  return serializeEditableToPrompt(container);
}

function setCaretTextOffset(root, offset) {
  if (!root || offset == null) return;
  const target = Math.max(0, offset);
  let pos = 0;

  const applyRange = (range) => {
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    root.focus?.();
  };

  const placeAtEnd = () => {
    const range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
    applyRange(range);
  };

  const walk = (node, isRoot = false) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.textContent?.length || 0;
      if (pos + len >= target) {
        const range = document.createRange();
        range.setStart(node, Math.min(target - pos, len));
        range.collapse(true);
        applyRange(range);
        return true;
      }
      pos += len;
      return false;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return false;

    if (node.classList?.contains('var-token')) {
      const name = node.getAttribute('data-var-name');
      const tokenLen = (name ? `{{${name}}}` : node.textContent || '').length;
      if (pos + tokenLen >= target) {
        const range = document.createRange();
        range.setStartAfter(node);
        range.collapse(true);
        applyRange(range);
        return true;
      }
      pos += tokenLen;
      return false;
    }

    if (node.tagName === 'BR') {
      if (pos + 1 >= target) {
        const range = document.createRange();
        if (target <= pos) {
          range.setStartBefore(node);
        } else {
          range.setStartAfter(node);
        }
        range.collapse(true);
        applyRange(range);
        return true;
      }
      pos += 1;
      return false;
    }

    const isBlock = EDITOR_BLOCK_TAGS.has(node.tagName);

    if (isBlock && !isRoot && isBlankLineBlock(node)) {
      if (pos + 1 >= target) {
        const range = document.createRange();
        if (target <= pos) {
          range.setStartBefore(node);
        } else {
          const next = node.nextSibling;
          if (next) {
            range.setStart(next, 0);
          } else {
            range.setStartAfter(node);
          }
        }
        range.collapse(true);
        applyRange(range);
        return true;
      }
      pos += 1;
      return false;
    }

    if (isBlock && !isRoot && pos > 0 && pos === target) {
      const range = document.createRange();
      range.setStart(node, 0);
      range.collapse(true);
      applyRange(range);
      return true;
    }

    for (const child of Array.from(node.childNodes)) {
      if (walk(child, false)) return true;
    }

    if (isBlock && !isRoot && node.nextSibling) {
      if (pos === target) {
        const range = document.createRange();
        range.setStartAfter(node);
        range.collapse(true);
        applyRange(range);
        return true;
      }
      pos += 1;
    }

    return false;
  };

  if (!walk(root, true)) {
    placeAtEnd();
  }
}

function applyEditorHtml(root, html, { force = false, restoreCaret = false } = {}) {
  if (!root || root.innerHTML === html) return;
  if (!force && isEditorFocused(root)) return;

  const caretOffset = restoreCaret ? getCaretTextOffset(root) : null;
  root.innerHTML = html;

  if (caretOffset != null) {
    requestAnimationFrame(() => setCaretTextOffset(root, caretOffset));
  }
}

function shouldForceTokenRefresh(root, text, effectiveVariables) {
  if (!root) return false;
  const domTokenCount = root.querySelectorAll('.var-token').length;
  const expectedPlaceholders = countDefinedPlaceholdersInText(text, effectiveVariables);
  return needsPlaceholderTokens(text, effectiveVariables) && domTokenCount < expectedPlaceholders;
}

/**
 * Variable-aware prompt editor: contentEditable surface with blue, clickable variable tokens.
 */
export default forwardRef(function VariablePromptEditor({
  value,
  onChange,
  variables = [],
  variableValues = {},
  onVariableClick,
  onBlur,
  minRows = 8,
  /** When true, tighter padding for use inside an outlined “field” on grey.50 (e.g. prompt modal). */
  embedded = false,
  /** Shown when the field is empty (contentEditable has no native placeholder). */
  placeholder = '',
}, ref) {
  const editorRef = useRef(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const effectiveVariables = useMemo(
    () => mergeVariablesFromTemplate(value ?? '', variables ?? []),
    [value, variables]
  );

  // When variables or preview values change, rebuild from canonical `value` (not innerText —
  // innerText loses `{{placeholders}}` once tokens render as default/preview text).
  useEffect(() => {
    if (!editorRef.current) return;
    const focused = isEditorFocused(editorRef.current);
    const canonical = focused
      ? serializeEditableToPrompt(editorRef.current)
      : (valueRef.current ?? '');
    const html = buildHtmlWithVariables(canonical, effectiveVariables, variableValues);
    const force = shouldForceTokenRefresh(editorRef.current, canonical, effectiveVariables);
    applyEditorHtml(editorRef.current, html, { force, restoreCaret: force && focused });
  }, [effectiveVariables, variableValues]);

  // When parent replaces prompt text (or load), sync if editor content differs.
  useEffect(() => {
    if (!editorRef.current) return;
    const focused = isEditorFocused(editorRef.current);
    const provided = value ?? '';
    const serialized = serializeEditableToPrompt(editorRef.current);
    const sourceText = focused ? serialized : provided;
    const nextHtml = buildHtmlWithVariables(sourceText, effectiveVariables, variableValues);

    if (serialized !== provided) {
      if (focused) {
        if (shouldForceTokenRefresh(editorRef.current, serialized, effectiveVariables)) {
          applyEditorHtml(editorRef.current, nextHtml, { force: true, restoreCaret: true });
        }
        return;
      }
      applyEditorHtml(editorRef.current, nextHtml, { force: true });
      return;
    }

    if (shouldForceTokenRefresh(editorRef.current, sourceText, effectiveVariables)) {
      applyEditorHtml(editorRef.current, nextHtml, { force: true, restoreCaret: focused });
    }
  }, [value, effectiveVariables, variableValues]);

  const handleInput = () => {
    if (!editorRef.current) return;
    const text = serializeEditableToPrompt(editorRef.current);
    onChange?.(text);
  };

  const flushAfterNativeEdit = () => {
    requestAnimationFrame(() => {
      if (!editorRef.current) return;
      const text = serializeEditableToPrompt(editorRef.current);
      onChange?.(text);
    });
  };

  useImperativeHandle(ref, () => ({
    flush: () => {
      if (!editorRef.current) return valueRef.current ?? '';
      const text = serializeEditableToPrompt(editorRef.current);
      onChange?.(text);
      return text;
    },
    focus: () => {
      editorRef.current?.focus();
    },
    getElement: () => editorRef.current,
  }));

  const handleBlur = (event) => {
    onBlur?.(event);
  };

  const handleClick = (e) => {
    if (!onVariableClick) return;
    let target = e.target;
    // Walk up a bit in case of nested spans
    for (let i = 0; i < 10 && target; i += 1) {
      if (target.classList && target.classList.contains('var-token')) {
        const varName = target.getAttribute('data-var-name');
        if (!varName) return;
        const variable = effectiveVariables.find(
          (v) => v && v.name && v.name.toLowerCase() === varName.toLowerCase()
        );
        if (variable) {
          e.stopPropagation();
          onVariableClick(variable);
        }
        return;
      }
      target = target.parentNode;
    }
  };

  const showPlaceholder = Boolean(placeholder && !String(value ?? '').trim());

  return (
    <Box
      sx={{
        p: embedded ? 0 : { xs: 2, sm: 3 },
        position: 'relative',
      }}
    >
      <Box sx={{ minWidth: 0 }} onClick={handleClick}>
        <Typography
          component="div"
          sx={{
            position: 'relative',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            margin: 0,
            ...instructionBodyTextSx,
            '& .var-token': variableTokenChipInteractiveSx,
          }}
        >
          {showPlaceholder && (
            <Box
              component="span"
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                right: 0,
                color: 'text.secondary',
                opacity: 0.55,
                pointerEvents: 'none',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                zIndex: 0,
              }}
            >
              {placeholder}
            </Box>
          )}
          <Box
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            onBlur={handleBlur}
            onPaste={flushAfterNativeEdit}
            onCut={flushAfterNativeEdit}
            sx={{
              outline: 'none',
              minHeight: `${minRows * 1.6}em`,
              position: 'relative',
              zIndex: 1,
              color: 'inherit',
              lineHeight: 'inherit',
              '& div, & p': {
                margin: 0,
                lineHeight: 'inherit',
              },
            }}
          />
        </Typography>
      </Box>
    </Box>
  );
});

